// Same-day ACH timing rules. See https://docs.column.com/ach/timing
//
// To settle same-day, an ACH must be submitted to Column no later than the final
// same-day window at 1:30 PM PT on a banking business day. Anything submitted after
// that cutoff (or on a weekend) is automatically processed with standard
// next-business-day timing, even when `same_day = true` is set. We surface this to
// the user so selecting "Same Day" past the cutoff isn't silently downgraded.
//
// Note: this is a best-effort client-side hint. It accounts for the time-of-day
// cutoff and weekends but not bank holidays (Column has no holiday calendar on the
// frontend); the network still auto-reverts a holiday submission to the next
// business day regardless of what we show here.

const SAME_DAY_CUTOFF_HOUR_PT = 13;
const SAME_DAY_CUTOFF_MINUTE_PT = 30;

// Human-readable cutoff used in UI copy so the number and the message stay in sync.
export const SAME_DAY_ACH_CUTOFF_LABEL = '1:30 PM PT';

// Returns true when a same-day ACH submitted "now" would miss the same-day window
// and instead settle the next business day.
export const isPastSameDayAchCutoff = (now: Date = new Date()): boolean => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(now);

  const partValue = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';

  const weekday = partValue('weekday');
  const hour = Number(partValue('hour'));
  const minute = Number(partValue('minute'));

  // Same-day ACH never settles on the weekend.
  if (weekday === 'Sat' || weekday === 'Sun') {
    return true;
  }

  if (hour > SAME_DAY_CUTOFF_HOUR_PT) {
    return true;
  }

  return hour === SAME_DAY_CUTOFF_HOUR_PT && minute >= SAME_DAY_CUTOFF_MINUTE_PT;
};
